/**
 * Helpers for Edge Function invoke errors (FunctionsHttpError).
 * Use to log or display the backend error/reason from 400/401 responses.
 */

export interface FunctionsErrorBody {
  error?: string;
  reason?: string;
  message?: string;
  /** Backend/GoTrue detail (e.g. "Invalid JWT", "JWT expired") */
  detail?: string;
}

/**
 * Parses the response body from an Edge Function error (e.g. 400/401).
 * error.context is the fetch Response; body is consumed once.
 */
export async function getFunctionsErrorBody(err: unknown): Promise<FunctionsErrorBody | null> {
  const context = (err as { context?: Response })?.context;
  if (!context || typeof (context as Response).json !== 'function') return null;
  try {
    const body = (await (context as Response).json()) as Record<string, unknown>;
    return {
      error: typeof body?.error === 'string' ? body.error : undefined,
      reason: typeof body?.reason === 'string' ? body.reason : undefined,
      message: typeof body?.message === 'string' ? body.message : undefined,
      detail: typeof body?.detail === 'string' ? body.detail : undefined,
    };
  } catch {
    return null;
  }
}

/** One-line message for console/UI: error + reason/detail if present. */
export function formatFunctionsError(body: FunctionsErrorBody | null): string {
  if (!body) return '';
  const main = body.error ?? body.message ?? body.detail;
  const parts = main ? [main] : [];
  if (body.reason) parts.push(`(${body.reason})`);
  if (body.detail && body.detail !== main) parts.push(body.detail);
  return parts.join(' ');
}

/** True when backend returned 400 "no telemetry" — expected for new setup; treat as no data, not error. */
export function isNoTelemetryError(body: FunctionsErrorBody | null): boolean {
  if (!body?.error) return false;
  const msg = body.error.toLowerCase();
  return (
    msg.includes('no plant telemetry') ||
    msg.includes('no recent telemetry')
  );
}

/** True when the request was aborted (e.g. navigation/unmount). Do not log or surface as error. */
export function isAbortError(err: unknown): boolean {
  if (err instanceof Error) return err.name === 'AbortError';
  if (typeof err === 'object' && err !== null && 'name' in err) return (err as { name: string }).name === 'AbortError';
  return false;
}

/** True when the Edge Function returned 404 (e.g. no telemetry yet). Avoid retries and treat as no data. */
export function is404Error(err: unknown): boolean {
  if (err == null) return false;
  const o = err as { status?: number; statusCode?: number; message?: string; context?: Response };
  if (o.status === 404 || o.statusCode === 404) return true;
  const msg = typeof o.message === 'string' ? o.message : '';
  if (msg.includes('404') || msg.includes('Not Found') || msg.includes('not found') || msg.includes('No telemetry yet')) return true;
  const res = o.context;
  if (res && typeof (res as Response).status === 'number' && (res as Response).status === 404) return true;
  return false;
}
